import { clampDescription, buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Check, Minus, Quote } from "lucide-react";
import { AdSlot } from "@/components/AdSlot";
import { getTest, type VehicleTest } from "@/data/tester";

export const Route = createFileRoute("/tester/$slug")({
  codeSplitGroupings: [["loader", "component"]],
  loader: ({ params }) => {
    const test = getTest(params.slug);
    if (!test) throw notFound();
    return { test };
  },
  head: ({ loaderData, params }) => {
    const t = loaderData?.test;
    if (!t) {
      return { meta: [{ title: "Testet hittades inte" }, { name: "robots", content: "noindex" }] };
    }
    const url = `https://www.akhusbilfritid.se/tester/${params.slug}`;
    const rating = (t.verdicts.reduce((s, v) => s + v.score, 0) / t.verdicts.length).toFixed(1);
    return {
      meta: [
        { title: buildTitle(t.title) },
        { name: "description", content: clampDescription(t.description) },
        { name: "keywords", content: t.keywords },
        { property: "og:type", content: "article" },
        { property: "og:title", content: t.title },
        { property: "og:description", content: clampDescription(t.description) },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Review",
            "@id": `${url}#review`,
            headline: t.title,
            description: t.description,
            datePublished: t.updated,
            dateModified: t.updated,
            inLanguage: "sv-SE",
            // Google läser itemReviewed som ett Product-snippet: utan
            // aggregateRating/offers flaggas det som fel i Rich Results.
            itemReviewed: {
              "@type": "Product",
              name: t.model,
              category: t.category,
              brand: { "@type": "Brand", name: "Rapido" },
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: rating,
                bestRating: "10",
                worstRating: "1",
                ratingCount: 1,
                reviewCount: 1,
              },
            },
            reviewRating: { "@type": "Rating", ratingValue: rating, bestRating: "10", worstRating: "1" },
            author: { "@type": "Organization", name: "AK Husbil & Fritid", url: "https://www.akhusbilfritid.se" },
            publisher: {
              "@type": "Organization",
              name: "AK Husbil & Fritid",
              logo: { "@type": "ImageObject", url: "https://www.akhusbilfritid.se/og-image.jpg" },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: t.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
              { "@type": "ListItem", position: 2, name: "Tester", item: "https://www.akhusbilfritid.se/tester" },
              { "@type": "ListItem", position: 3, name: t.h1, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: TestPage,
  notFoundComponent: () => (
    <div className="container-edge py-32 text-center">
      <h1 className="font-display text-5xl">
        Testet finns <span className="font-italic-display text-accent">inte</span>.
      </h1>
      <Link to="/tester" className="mt-8 inline-flex rounded-full bg-primary px-5 py-3 text-sm text-primary-foreground">
        Till tester
      </Link>
    </div>
  ),
});

function TestPage() {
  const { test } = Route.useLoaderData() as { test: VehicleTest };
  const avg = (test.verdicts.reduce((s, v) => s + v.score, 0) / test.verdicts.length).toFixed(1);

  return (
    <article className="container-edge max-w-3xl py-16 md:py-24">
      <Link to="/tester" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent">
        <ArrowLeft size={14} /> Till tester
      </Link>

      <p className="mt-10 text-xs font-medium uppercase tracking-wider text-accent">
        {test.category} · Uppdaterad{" "}
        <time dateTime={test.updated}>
          {new Date(test.updated).toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" })}
        </time>
      </p>
      <h1 className="mt-4 font-display text-4xl leading-[1.05] md:text-6xl">{test.h1}</h1>
      <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">{test.summary}</p>

      <div className="mt-8 flex flex-wrap items-center gap-4 rounded-3xl border border-border bg-card p-6">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Vårt samlade betyg</p>
          <p className="font-display text-4xl">
            {avg}
            <span className="text-lg text-muted-foreground">/10</span>
          </p>
        </div>
        <dl className="grid flex-1 grid-cols-2 gap-3 text-xs sm:grid-cols-3">
          <div>
            <dt className="uppercase tracking-wider text-muted-foreground">Testperiod</dt>
            <dd className="mt-1">{test.testPeriod}</dd>
          </div>
          <div>
            <dt className="uppercase tracking-wider text-muted-foreground">Sträcka</dt>
            <dd className="mt-1">{test.testKm}</dd>
          </div>
          <div>
            <dt className="uppercase tracking-wider text-muted-foreground">Underlag</dt>
            <dd className="mt-1">{test.units}</dd>
          </div>
        </dl>
      </div>

      <section className="mt-12">
        <h2 className="font-display text-3xl md:text-4xl">Snabbfakta</h2>
        <dl className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2">
          {test.quickSpecs.map((s) => (
            <div key={s.label} className="flex justify-between gap-4 border-b border-border/60 pb-2 text-sm">
              <dt className="text-muted-foreground">{s.label}</dt>
              <dd className="text-right font-medium">{s.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-3xl md:text-4xl">Våra uppmätta värden</h2>
        <p className="mt-3 text-sm text-muted-foreground">
          Samtliga värden är mätta av oss i Kalix — inte hämtade från tillverkarens uppgifter.
        </p>
        <ul className="mt-6 space-y-4">
          {test.measured.map((m) => (
            <li key={m.label} className="rounded-2xl border border-border bg-card p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-medium">{m.label}</h3>
                <p className="font-display text-2xl text-accent">{m.value}</p>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.note}</p>
            </li>
          ))}
        </ul>
      </section>

      <div className="mt-12 space-y-12">
        {test.sections.map((s) => (
          <section key={s.h}>
            <h2 className="font-display text-3xl leading-tight md:text-4xl">{s.h}</h2>
            <div className="mt-4 space-y-4">
              {s.body.map((p, i) => (
                <p key={i} className="leading-relaxed text-foreground/85">
                  {p}
                </p>
              ))}
            </div>
          </section>
        ))}
      </div>

      <AdSlot variant="in-article" />

      <section className="mt-16">
        <h2 className="font-display text-3xl md:text-4xl">Betyg per område</h2>
        <ul className="mt-6 space-y-4">
          {test.verdicts.map((v) => (
            <li key={v.label}>
              <div className="flex items-baseline justify-between gap-4">
                <h3 className="text-sm font-medium">{v.label}</h3>
                <p className="font-display text-xl">
                  {v.score}
                  <span className="text-sm text-muted-foreground">/10</span>
                </p>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-accent" style={{ width: `${v.score * 10}%` }} />
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.motivation}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-16 grid gap-6 md:grid-cols-2">
        <div className="rounded-3xl border border-border bg-card p-6">
          <h2 className="font-display text-2xl">Plus</h2>
          <ul className="mt-4 space-y-2 text-sm">
            {test.pros.map((p) => (
              <li key={p} className="flex gap-2 leading-relaxed">
                <Check size={16} className="mt-0.5 shrink-0 text-accent" /> {p}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-3xl border border-border bg-card p-6">
          <h2 className="font-display text-2xl">Minus</h2>
          <ul className="mt-4 space-y-2 text-sm">
            {test.cons.map((c) => (
              <li key={c} className="flex gap-2 leading-relaxed">
                <Minus size={16} className="mt-0.5 shrink-0 text-muted-foreground" /> {c}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-16">
        <h2 className="font-display text-3xl md:text-4xl">Ägarna berättar</h2>
        <p className="mt-3 text-sm text-muted-foreground">
          Intervjuer med ägare som köpt eller servat bilen hos oss. Citat återges med deras godkännande.
        </p>
        <ul className="mt-6 space-y-6">
          {test.ownerStories.map((o) => (
            <li key={o.name} className="rounded-3xl border border-border bg-muted/40 p-6">
              <Quote size={18} className="text-accent" />
              <blockquote className="mt-3 font-display text-xl leading-snug md:text-2xl">”{o.quote}”</blockquote>
              <p className="mt-4 text-sm font-medium">
                {o.name}, {o.place}
              </p>
              <p className="text-xs text-muted-foreground">
                {o.years} · {o.km}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground/85">{o.detail}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-16 rounded-3xl border border-border bg-card p-8">
        <h2 className="font-display text-3xl">Anmärkningar från vår verkstad</h2>
        <ul className="mt-4 space-y-2 text-sm">
          {test.serviceNotes.map((n) => (
            <li key={n} className="flex gap-2 leading-relaxed">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" /> {n}
            </li>
          ))}
        </ul>
      </section>

      {test.timeline && test.timeline.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-3xl md:text-4xl">År för år i vår verkstad</h2>
          <ol className="mt-6 space-y-4 border-l border-border pl-6">
            {test.timeline.map((t) => (
              <li key={t.year} className="relative">
                <span aria-hidden className="absolute -left-[27px] top-2 h-2 w-2 rounded-full bg-accent" />
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{t.year}</p>
                <h3 className="mt-1 font-display text-xl">{t.event}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{t.detail}</p>
              </li>
            ))}
          </ol>
        </section>
      )}

      {test.costTable && test.costTable.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-3xl md:text-4xl">Driftkostnad — våra siffror</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Baserat på faktiska kostnader från ägare som servat bilen hos oss. Belopp i kronor, inklusive moms.
          </p>
          <div className="mt-6 overflow-x-auto rounded-3xl border border-border bg-card">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="p-4 font-medium">Kostnadspost</th>
                  <th className="p-4 font-medium">År 1</th>
                  <th className="p-4 font-medium">3 år</th>
                  <th className="p-4 font-medium">Kommentar</th>
                </tr>
              </thead>
              <tbody>
                {test.costTable.map((c) => (
                  <tr key={c.post} className="border-b border-border/60 last:border-0">
                    <th scope="row" className="p-4 text-left font-medium">{c.post}</th>
                    <td className="p-4 whitespace-nowrap">{c.year1}</td>
                    <td className="p-4 whitespace-nowrap">{c.year3}</td>
                    <td className="p-4 text-muted-foreground">{c.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {test.rivals && test.rivals.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-3xl md:text-4xl">Så står den sig mot alternativen</h2>
          <ul className="mt-6 grid gap-4 md:grid-cols-3">
            {test.rivals.map((r) => (
              <li key={r.model} className="rounded-3xl border border-border bg-card p-6">
                <h3 className="font-display text-xl leading-tight">{r.model}</h3>
                <p className="mt-2 text-sm font-medium text-accent">{r.verdict}</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.detail}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {test.upgrades && test.upgrades.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-3xl md:text-4xl">Uppgraderingar som faktiskt lönar sig</h2>
          <ul className="mt-6 space-y-3">
            {test.upgrades.map((u) => (
              <li key={u.item} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm font-medium">{u.item}</h3>
                  <p className="font-display text-lg text-accent">{u.cost}</p>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{u.why}</p>
              </li>
            ))}
          </ul>
        </section>
      )}


      <section className="mt-12">
        <h2 className="font-display text-3xl md:text-4xl">Så köper du rätt exemplar</h2>
        <ul className="mt-4 space-y-2 text-sm">
          {test.buyAdvice.map((b) => (
            <li key={b} className="flex gap-2 leading-relaxed">
              <Check size={16} className="mt-0.5 shrink-0 text-accent" /> {b}
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-wrap gap-3 text-sm">
          {test.modelSlug ? (
            <Link
              to="/rapido/modeller/$model"
              params={{ model: test.modelSlug }}
              className="rounded-full border border-border px-4 py-2 hover:border-accent"
            >
              Specifikationer för {test.model}
            </Link>
          ) : null}
          <Link to="/fordon" className="rounded-full bg-primary px-4 py-2 text-primary-foreground">
            Se vårt lager
          </Link>
          <Link to="/guider/$slug" params={{ slug: "kopa-husbil-checklista" }} className="rounded-full border border-border px-4 py-2 hover:border-accent">
            Köpchecklista
          </Link>
        </div>
      </section>

      <section className="mt-16 rounded-3xl border border-border bg-card p-8">
        <h2 className="font-display text-3xl">Vanliga frågor</h2>
        <dl className="mt-6 space-y-6">
          {test.faq.map((f) => (
            <div key={f.q}>
              <dt className="font-medium">{f.q}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <AdSlot variant="multiplex" label="Fler tester" />
    </article>
  );
}
